import React, { useEffect, useState } from "react";
import type { NodeProps, Node } from "@xyflow/react";

import type { LatchData } from "../../../api";
import { ActionFlag, CheckFlagCondition } from "../../../api";
import type { ExtraNodeData, InterfaceToRecord } from "./action-config-node";
import ActionConfigNode from "./action-config-node";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { IDropdownOption } from "@fluentui/react";
import { Dropdown, Separator } from "@fluentui/react";
import { useUpdateNodeData } from "@/lib/hooks/reactflow";

type LatchNode = Node<InterfaceToRecord<LatchData & ExtraNodeData>>;

export default function Latch(props: NodeProps<LatchNode>): JSX.Element {
    const { t }: { t: TFunction } = useTranslation();

    const [actionFlagSet, setActionFlagSet] = useState(
        props.data.setFlag ?? ActionFlag.STATIC_FLAG_ZERO,
    );
    const [conditionSet, setConditionSet] = useState(
        props.data.setCondition ?? CheckFlagCondition.ALL_ANTENNAS_AIN_ZONE1,
    );

    const [actionFlagReset, setActionFlagReset] = useState(
        props.data.resetFlag ?? ActionFlag.STATIC_FLAG_ZERO,
    );
    const [conditionReset, setConditionReset] = useState(
        props.data.resetCondition ?? CheckFlagCondition.ALL_ANTENNAS_AIN_ZONE1,
    );

    const updateNode = useUpdateNodeData<LatchNode>(props.id);

    // Effect to update props data when internal state changes
    useEffect(() => {
        updateNode({
            setFlag: actionFlagSet,
            setCondition: conditionSet,
            resetFlag: actionFlagReset,
            resetCondition: conditionReset,
        });
    }, [actionFlagSet, conditionSet, actionFlagReset, conditionReset, updateNode]);

    const dropdownOptionsActionflag: IDropdownOption[] = Object.values(ActionFlag).map(
        (key, index) => ({
            key,
            text: t(`component.actionConfigNodes.checkFlag.actionFlag.${index}`),
        }),
    );

    const dropdownOptionsCheckFlagCondition: IDropdownOption[] = Object.values(
        CheckFlagCondition,
    ).map((key, index) => ({
        key,
        text: t(`component.actionConfigNodes.checkFlag.condition.${index}`),
    }));

    return (
        <ActionConfigNode nodeKind={props.type} {...props.data}>
            <Dropdown
                label={"Set via"}
                options={dropdownOptionsActionflag}
                selectedKey={actionFlagSet}
                onChange={(_e, option) => setActionFlagSet(option?.key as ActionFlag)}
                styles={{ dropdown: { width: 400 } }}
            />

            <Dropdown
                label={t("component.actionConfigNodes.checkFlag.condition.label")}
                options={dropdownOptionsCheckFlagCondition}
                selectedKey={conditionSet}
                onChange={(_e, option) => setConditionSet(option?.key as CheckFlagCondition)}
                styles={{ dropdown: { width: 400 } }}
            />
            <Separator />
            <Dropdown
                label={"Reset via"}
                options={dropdownOptionsActionflag}
                selectedKey={actionFlagReset}
                onChange={(_e, option) => setActionFlagReset(option?.key as ActionFlag)}
                styles={{ dropdown: { width: 400 } }}
            />

            <Dropdown
                label={t("component.actionConfigNodes.checkFlag.condition.label")}
                options={dropdownOptionsCheckFlagCondition}
                selectedKey={conditionReset}
                onChange={(_e, option) => setConditionReset(option?.key as CheckFlagCondition)}
                styles={{ dropdown: { width: 400 } }}
            />
        </ActionConfigNode>
    );
}
